import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import clsx from 'clsx';
import { Camera, Check, CloudOff, Image, ImageOff, Maximize2, PackageSearch, Plus, RefreshCw, Trash } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAppSelector } from '@/app/hooks';
import api, { getErrorMessage } from '@/services/api';
import { ApiResponse, PagedResponse, StockRequest, StockRequestProduct, StockRequestStatus, StockRequestType } from '@/services/types';
import { API_ENDPOINTS } from '@/utils/constants';
import { formatFileSize } from '@/utils/formatters';
import { classifyStockRequestError, rawFromMessage, shrinkImage, toDisplayQty } from '@/utils/stockRequest';
import { Alert, Badge, Button, Chip, IconButton, Input, LoadingSkeleton, Overline, SegmentedControl, Select, Sheet } from '@/components/ui';
import { PageShell } from '@/components/layout/PageShell';
import { useIsLandscape } from '@/hooks/useMediaQuery';

const MAX_IMAGE_BYTES = 1 * 1024 * 1024;
const PAGE_SIZE = 20;
type Filter = 'ALL' | StockRequestStatus;
const FILTERS: Filter[] = ['ALL', 'PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'];
const STATUS_TONE: Record<StockRequestStatus, 'warning' | 'success' | 'danger' | 'neutral'> = { PENDING: 'warning', APPROVED: 'success', REJECTED: 'danger', CANCELLED: 'neutral' };
const TYPE_TONE: Record<StockRequestType, 'success' | 'danger'> = { ADD: 'success', REMOVE: 'danger' };

/** Stock-request routes return meta.errors as a plain string on failure. */
const apiError = (err: unknown) => {
    if (axios.isAxiosError(err)) {
        const errors = err.response?.data?.meta?.errors;
        if (typeof errors === 'string' && errors) return errors;
    }
    return getErrorMessage(err);
};

/** createdAt is a naive server-local string; formatters' toLocalDate would shift it by +7h. */
const whenOf = (iso: string) => new Date(iso).toLocaleString('id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

const openProof = (url: string) => window.open(url, '_blank', 'noopener');

const fmtQty = (n: number) => n.toLocaleString('id-ID', { maximumFractionDigits: 2 });
const qtyOf = (r: StockRequest, raw: number | null | undefined) => (raw == null ? '—' : `${fmtQty(toDisplayQty(raw, r.stockUsage))} ${r.usageUnit ?? ''}`.trim());

/**
 * Stock adjustment request — filterable list with a detail pane (lg) / sheet (phone), and a submit form
 * (photo · product/type/quantity/description) as a second view. Mirrors ReimbursementPage.
 */
const StockRequestPage: React.FC = () => {
    const { t } = useTranslation();
    const { selectedShop } = useAppSelector((s) => s.auth);
    const isLandscape = useIsLandscape();
    const [products, setProducts] = useState<StockRequestProduct[]>([]);
    const [entries, setEntries] = useState<StockRequest[]>([]);
    const [page, setPage] = useState(0);
    const [last, setLast] = useState(true);
    const [filter, setFilter] = useState<Filter>('ALL');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [view, setView] = useState<'list' | 'submit'>('list');
    // submit form
    const [photo, setPhoto] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [productId, setProductId] = useState('');
    const [type, setType] = useState<StockRequestType>('REMOVE');
    const [quantity, setQuantity] = useState('');
    const [description, setDescription] = useState('');
    const [photoError, setPhotoError] = useState<string | null>(null);
    const [productError, setProductError] = useState<string | null>(null);
    const [quantityError, setQuantityError] = useState<string | null>(null);
    const [typeError, setTypeError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [uploadFailed, setUploadFailed] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);

    const shopId = selectedShop?.shopId;

    const loadProducts = useCallback(async () => {
        if (!shopId) return;
        try {
            const res = await api.get<ApiResponse<StockRequestProduct[]>>(API_ENDPOINTS.STOCK_REQUEST_PRODUCTS(shopId));
            const list = res.data.data || [];
            setProducts(list);
            // The item may have been deleted since it was picked.
            setProductId((id) => (list.some((p) => String(p.productPointId) === id) ? id : ''));
        } catch (e) {
            setError(apiError(e));
        }
    }, [shopId]);

    const load = useCallback(async (p: number, replace: boolean) => {
        if (!shopId) return;
        setLoading(true);
        setError(null);
        try {
            const res = await api.get<ApiResponse<PagedResponse<StockRequest>>>(API_ENDPOINTS.STOCK_REQUEST(shopId), {
                params: { status: filter === 'ALL' ? undefined : filter, page: p, size: PAGE_SIZE },
            });
            const content = res.data.data.content || [];
            setEntries((prev) => (replace ? content : [...prev, ...content]));
            setLast(res.data.data.page?.last ?? content.length < PAGE_SIZE);
            setPage(p);
        } catch (e) {
            setError(apiError(e));
        } finally {
            setLoading(false);
        }
    }, [shopId, filter]);

    const refresh = useCallback(() => { loadProducts(); load(0, true); }, [loadProducts, load]);

    useEffect(() => { refresh(); }, [refresh]);
    useEffect(() => {
        if (!photo) { setPreview(null); return; }
        const url = URL.createObjectURL(photo);
        setPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [photo]);

    if (!selectedShop || !shopId) return null;

    const selected = entries.find((e) => e.id === selectedId) ?? (isLandscape ? entries[0] : undefined) ?? null;
    const product = products.find((p) => String(p.productPointId) === productId);
    const unit = product?.usageUnit ?? '';
    const available = product ? toDisplayQty(product.stock, product.stockUsage) : 0;
    const qtyNum = Number(quantity);
    const qtyValid = quantity !== '' && Number.isFinite(qtyNum) && qtyNum > 0;
    const exceeds = type === 'REMOVE' && qtyValid && qtyNum > available;
    const photoTooLarge = !!photo && photo.size > MAX_IMAGE_BYTES;
    const noProducts = products.length === 0;
    const canSubmit = !!photo && !photoTooLarge && !!product && qtyValid && !exceeds && !noProducts;

    const productOptions = products.map((p) => ({
        value: String(p.productPointId),
        label: `${p.productName} · ${t('stockRequest.available', { n: fmtQty(toDisplayQty(p.stock, p.stockUsage)), unit: p.usageUnit ?? '' })}`,
    }));

    const pickPhoto = async (f: File | null) => {
        const file = f ? await shrinkImage(f, MAX_IMAGE_BYTES) : null;
        setPhoto(file);
        setPhotoError(file && file.size > MAX_IMAGE_BYTES ? t('stockRequest.photoTooLarge') : null);
        setUploadFailed(false);
    };
    const resetForm = () => { setPhoto(null); setProductId(''); setType('REMOVE'); setQuantity(''); setDescription(''); setPhotoError(null); setProductError(null); setQuantityError(null); setTypeError(null); setFormError(null); setUploadFailed(false); };
    const openSubmit = () => { resetForm(); setView('submit'); };
    const statusLabel = (s: StockRequestStatus) => t(`stockRequest.status.${s}` as 'stockRequest.status.PENDING');
    const typeLabel = (s: StockRequestType) => t(`stockRequest.type.${s}` as 'stockRequest.type.ADD');
    const exceedsMsg = (n: number) => t('stockRequest.quantityExceeds', { n: fmtQty(n), unit });

    const submit = async () => {
        setFormError(null); setProductError(null); setQuantityError(null); setTypeError(null);
        if (!photo) { setPhotoError(t('stockRequest.photoRequired')); return; }
        if (photoTooLarge) { setPhotoError(t('stockRequest.photoTooLarge')); return; }
        if (!product) { setProductError(t('stockRequest.productRequired')); return; }
        if (!qtyValid) { setQuantityError(t('stockRequest.quantityInvalid')); return; }
        if (exceeds) { setQuantityError(exceedsMsg(available)); return; }
        setSubmitting(true);
        try {
            const form = new FormData();
            form.append('proof', photo, 'proof.jpg');
            form.append('productPointId', String(product.productPointId));
            form.append('type', type);
            form.append('quantity', String(qtyNum));
            if (description.trim()) form.append('description', description.trim());
            await api.post<ApiResponse<StockRequest>>(API_ENDPOINTS.STOCK_REQUEST(shopId), form, { headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success(t('stockRequest.submitted'));
            resetForm();
            setView('list');
            if (filter === 'ALL') refresh(); else setFilter('ALL'); // filter change refetches via the effect
        } catch (e) {
            const msg = apiError(e);
            switch (classifyStockRequestError(msg)) {
                case 'photoRequired': setPhotoError(t('stockRequest.photoRequired')); setPhoto(null); break;
                case 'photoTooLarge': setPhotoError(t('stockRequest.photoTooLarge')); setPhoto(null); break;
                case 'uploadFailed': setUploadFailed(true); break;
                case 'photo': setPhotoError(msg); setPhoto(null); break;
                case 'product': setProductError(t('stockRequest.productNotInShop')); loadProducts(); break;
                case 'quantity': {
                    const raw = rawFromMessage(msg);
                    setQuantityError(raw != null ? exceedsMsg(toDisplayQty(raw, product.stockUsage)) : msg);
                    break;
                }
                case 'type': setTypeError(msg); break;
                case 'inactive': setFormError(t('stockRequest.inactiveUser')); break;
                default: setFormError(msg);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const detail = (r: StockRequest) => {
        const facts: [string, string][] = [
            [t('stockRequest.recordedBy'), r.requesterName ?? r.requesterClientId ?? '—'],
            [t('stockRequest.time'), whenOf(r.createdAt)],
            [t('stockRequest.stockBefore'), qtyOf(r, r.stockBefore)],
            ...(r.status === 'APPROVED' ? [[t('stockRequest.stockAfter'), qtyOf(r, r.stockAfter)] as [string, string]] : []),
            [t('stockRequest.reviewedAt'), r.reviewedAt ? whenOf(r.reviewedAt) : '—'],
        ];
        return (
            <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2.5">
                    <Overline className="flex-1">#{r.id}</Overline>
                    <Badge tone={TYPE_TONE[r.type]}>{typeLabel(r.type)}</Badge>
                    <Badge tone={STATUS_TONE[r.status]} dot>{statusLabel(r.status)}</Badge>
                </div>
                <div className="flex flex-col gap-1">
                    <span className="text-[38px] font-black tnum leading-[1.1] tracking-[-0.02em] text-ink-brand">{r.type === 'ADD' ? '+' : '−'}{qtyOf(r, r.quantity)}</span>
                    <span className="text-[19px] font-black">{r.productName}</span>
                    {r.note && <span className="text-sm leading-relaxed text-ink-secondary">{r.note}</span>}
                </div>
                {r.status === 'REJECTED' && r.reviewerNote && <Alert variant="error" title={t('stockRequest.rejectedNote')} message={r.reviewerNote} />}
                {r.status === 'PENDING' && <Alert variant="warning" message={t('stockRequest.pendingHint')} />}
                {r.proofImageUrl && (
                    <button type="button" onClick={() => openProof(r.proofImageUrl!)} className="relative h-[168px] shrink-0 overflow-hidden rounded-lg border border-line-subtle bg-surface-sunken">
                        <img src={r.proofImageUrl} alt="" className="h-full w-full object-cover" />
                        <span className="absolute bottom-2.5 right-2.5 flex h-[30px] items-center gap-1.5 rounded-pill bg-[#2d1227]/70 px-3 text-xs font-extrabold text-white backdrop-blur"><Maximize2 className="h-[13px] w-[13px]" />{t('stockRequest.viewProof')}</span>
                    </button>
                )}
                <div className="flex flex-col border-t border-line-subtle">
                    {facts.map(([k, v]) => (
                        <div key={k} className="flex justify-between gap-3 border-b border-line-subtle py-2.5 text-sm"><span className="text-ink-muted">{k}</span><span className="text-right font-extrabold">{v}</span></div>
                    ))}
                </div>
            </div>
        );
    };

    if (view === 'submit') {
        const photoSlot = photoTooLarge ? (
            <div className="flex min-h-[220px] flex-1 flex-col items-center justify-center gap-3 rounded-xl border-2 border-status-danger bg-status-danger-soft p-6 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-surface-card text-status-danger"><ImageOff className="h-7 w-7" /></span>
                <span className="text-[17px] font-black text-danger-700">{t('stockRequest.photoTooLarge')} · {formatFileSize(photo!.size)}</span>
                <span className="text-[13px] leading-relaxed">{t('stockRequest.photoTooLargeHint')}</span>
            </div>
        ) : preview ? (
            <div className="relative min-h-[220px] flex-1 overflow-hidden rounded-xl border border-line-subtle bg-surface-sunken">
                <img src={preview} alt="" className="absolute inset-0 h-full w-full object-cover" />
                <span className="absolute left-3 top-3 flex h-7 items-center gap-1.5 rounded-pill bg-surface-card px-2.5 text-xs font-extrabold text-success-700 shadow-xs"><Check className="h-[13px] w-[13px]" />{formatFileSize(photo!.size)}</span>
            </div>
        ) : (
            <button type="button" onClick={() => fileRef.current?.click()} className={clsx('flex min-h-[220px] flex-1 flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed bg-surface-card p-6 text-center hover:border-accent-500 hover:bg-accentsoft', photoError ? 'border-status-danger' : 'border-line')}>
                <span className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-accent-500 text-ink-onAccent shadow-accent"><Camera className="h-[30px] w-[30px]" /></span>
                <span className="text-[17px] font-black">{t('stockRequest.takePhoto')}</span>
                <span className="text-[13px] leading-relaxed text-ink-muted">{t('stockRequest.photoHint')}</span>
            </button>
        );
        const cta = <Button variant="primary" size="lg" fullWidth disabled={!canSubmit} loading={submitting} onClick={submit}>{uploadFailed ? t('stockRequest.resend') : t('stockRequest.send')}</Button>;
        return (
            <PageShell
                close
                title={t('stockRequest.submit')}
                subtitle={selectedShop.shopName}
                onBack={() => setView('list')}
                footer={<div className="lg:hidden">{cta}</div>}
            >
                <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { pickPhoto(e.target.files?.[0] ?? null); e.target.value = ''; }} />
                <div className="flex flex-col gap-4 pb-5 lg:grid lg:h-full lg:grid-cols-[340px_minmax(0,1fr)] lg:gap-5 lg:pb-6">
                    <div className="flex flex-col gap-2.5 lg:min-h-0">
                        <span className="text-[13px] font-extrabold text-ink-secondary">{t('stockRequest.photo')} <span className="text-status-danger">*</span></span>
                        {photoSlot}
                        {photoError && !photoTooLarge && <p className="text-xs font-semibold text-status-danger">{photoError}</p>}
                        {photo && (
                            <div className="flex gap-2.5">
                                <Button variant={photoTooLarge ? 'primary' : 'secondary'} fullWidth icon={<Camera />} onClick={() => fileRef.current?.click()}>{t('stockRequest.retake')}</Button>
                                {!photoTooLarge && <Button variant="ghost" icon={<Trash />} onClick={() => pickPhoto(null)}>{t('stockRequest.remove')}</Button>}
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col gap-4 lg:min-w-0">
                        {noProducts && <Alert variant="warning" message={t('stockRequest.noProducts')} />}
                        {uploadFailed && <Alert variant="error" title={t('stockRequest.uploadFailed')} message={<span className="flex items-center gap-2"><CloudOff className="h-4 w-4 shrink-0" />{t('stockRequest.uploadFailedHint')}</span>} />}
                        {formError && <Alert variant="error" message={formError} />}
                        <Select label={t('stockRequest.product')} required options={productOptions} placeholder={t('stockRequest.productPlaceholder')} value={productId} disabled={noProducts} onChange={(e) => { setProductId(e.target.value); setProductError(null); setQuantityError(null); }} error={productError ?? undefined} />
                        <div className="flex flex-col gap-2">
                            <span className="text-[13px] font-extrabold text-ink-secondary">{t('stockRequest.typeLabel')} <span className="text-status-danger">*</span></span>
                            <SegmentedControl<StockRequestType> fullWidth value={type} onChange={(v) => { setType(v); setTypeError(null); setQuantityError(null); }} options={[{ value: 'ADD', label: typeLabel('ADD') }, { value: 'REMOVE', label: typeLabel('REMOVE') }]} aria-label={t('stockRequest.typeLabel')} />
                            {typeError && <p className="text-xs font-semibold text-status-danger">{typeError}</p>}
                        </div>
                        <Input
                            label={t('stockRequest.quantity')}
                            required
                            type="number"
                            step="any"
                            min="0"
                            inputMode="decimal"
                            placeholder="0"
                            value={quantity}
                            disabled={!product}
                            onChange={(e) => { setQuantity(e.target.value); setQuantityError(null); }}
                            error={quantityError ?? (exceeds ? exceedsMsg(available) : undefined)}
                            helperText={product ? t('stockRequest.available', { n: fmtQty(available), unit }) : undefined}
                            rightIcon={unit ? <span className="text-sm font-extrabold">{unit}</span> : undefined}
                            fieldSize="lg"
                            className="!text-xl !font-extrabold tnum"
                        />
                        <Input label={`${t('stockRequest.description')} ${t('stockRequest.optional')}`} multiline rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('stockRequest.descriptionPlaceholder')} />
                        <div className="hidden flex-1 lg:block" />
                        <Alert variant="brand" message={t('stockRequest.submitHint')} />
                        <div className="hidden lg:block">{cta}</div>
                    </div>
                </div>
            </PageShell>
        );
    }

    const submitButton = (props: { size?: 'md' | 'lg'; fullWidth?: boolean; className?: string }) => (
        <Button variant="primary" icon={<Plus />} onClick={openSubmit} disabled={noProducts} {...props}>{t('stockRequest.submit')}</Button>
    );

    return (
        <div className="flex h-full min-h-0 flex-1">
            <PageShell
                title={t('stockRequest.title')}
                subtitle={selectedShop.shopName}
                backTo="/settings"
                className="lg:border-r lg:border-line-subtle"
                actions={<>
                    <IconButton variant="card" label={t('common.refresh')} onClick={refresh} disabled={loading}><RefreshCw className={clsx(loading && 'animate-spin')} /></IconButton>
                    {submitButton({ className: 'hidden lg:inline-flex' })}
                </>}
                footer={<div className="lg:hidden">{submitButton({ size: 'lg', fullWidth: true })}</div>}
            >
                <div className="flex flex-col gap-3 pb-5">
                    {error && <Alert variant="error" message={error} />}
                    {!error && noProducts && <Alert variant="warning" message={t('stockRequest.noProducts')} />}
                    <div className="flex gap-2 overflow-x-auto">
                        {FILTERS.map((f) => (
                            <Chip key={f} selected={filter === f} onClick={() => setFilter(f)}>{f === 'ALL' ? t('stockRequest.filterAll') : statusLabel(f)}</Chip>
                        ))}
                    </div>
                    {loading && entries.length === 0 && [1, 2, 3].map((i) => <LoadingSkeleton key={i} height={72} />)}
                    {!loading && entries.length === 0 && (
                        <div className="flex flex-col items-center gap-3 py-12 text-center">
                            <span className="flex h-[88px] w-[88px] items-center justify-center rounded-full bg-brandsoft text-ink-brand"><PackageSearch className="h-[38px] w-[38px]" /></span>
                            <span className="text-[22px] font-black">{t('stockRequest.empty')}</span>
                            <span className="max-w-[380px] text-[15px] leading-relaxed text-ink-secondary">{t('stockRequest.emptyHint')}</span>
                        </div>
                    )}
                    {entries.map((r) => {
                        const active = isLandscape && selected?.id === r.id;
                        return (
                            <button
                                key={r.id}
                                type="button"
                                onClick={() => { setSelectedId(r.id); if (!isLandscape) setDetailOpen(true); }}
                                className={clsx('flex items-center gap-3.5 rounded-lg px-4 py-3 text-left', active ? 'border-2 border-accent-500 bg-accentsoft' : 'border border-line-subtle bg-surface-card')}
                            >
                                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-surface-sunken text-ink-brand"><PackageSearch className="h-5 w-5" /></span>
                                <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
                                    <span className="truncate text-base font-extrabold">{r.productName}</span>
                                    <span className="flex items-center gap-1.5 text-[13px] text-ink-muted">{r.proofImageUrl && <Image className="h-3.5 w-3.5" />}{r.requesterName ?? r.requesterClientId ?? '—'} · {whenOf(r.createdAt)}</span>
                                    {r.status === 'REJECTED' && r.reviewerNote && <span className="truncate text-xs text-ink-muted">{r.reviewerNote}</span>}
                                </span>
                                <span className="flex flex-col items-end gap-1.5">
                                    <span className="flex items-center gap-1.5"><Badge tone={TYPE_TONE[r.type]}>{typeLabel(r.type)}</Badge><span className="text-base font-black tnum">{qtyOf(r, r.quantity)}</span></span>
                                    <Badge tone={STATUS_TONE[r.status]} dot>{statusLabel(r.status)}</Badge>
                                </span>
                            </button>
                        );
                    })}
                    {!last && <Button variant="outline" onClick={() => load(page + 1, false)} loading={loading} className="mt-2 self-center">{t('common.loadMore')}</Button>}
                </div>
            </PageShell>

            <aside className="hidden w-[420px] shrink-0 overflow-y-auto bg-surface-card p-6 lg:block">
                {selected ? detail(selected) : (
                    <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-ink-muted">
                        <PackageSearch className="h-9 w-9 text-primary-200" />
                        <p className="text-sm font-bold">{t('stockRequest.selectHint')}</p>
                    </div>
                )}
            </aside>

            <Sheet isOpen={detailOpen && !isLandscape && !!selected} onClose={() => setDetailOpen(false)} showCloseButton={false}>
                {selected && detail(selected)}
            </Sheet>
        </div>
    );
};

export default StockRequestPage;
